import type React from "react";
import { useState } from "react";
import { Button } from "./Button";
import { LoaderCircle } from "lucide-react";
import { getAuthHeader } from "../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "./CustomToast";
import SocialMediaHelpOverlay from "./SocialMediaHelpOverlay";
import { connectionGuidePref } from "../../utils/prefCookie";

interface ConnectSocialAccountsButtonProps {
    text: string;
    redirectUrl: string;
    disabled?: boolean;
}

const ConnectSocialAccountsButton: React.FC<
    ConnectSocialAccountsButtonProps
> = ({ text, redirectUrl, disabled }) => {
    const [connectLoading, setConnectLoading] = useState(false);
    const [showWizard, setShowWizard] = useState(false);

    const proceedToConnection = async () => {
        setConnectLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(
                `${backendUrl}/socials/link-social-url`,
                { redirectUrl },
                { headers }
            );

            if (response.status === 200 && response.data) {
                const connectUrl = response.data;
                if (connectUrl) {
                    window.location.href = connectUrl;
                } else {
                    throw new Error("Invalid URL received from the server.");
                }
            } else {
                throw new Error(
                    "Failed to fetch social connect URL. Please try again or go to settings to connect your social accounts."
                );
            }
        } catch (error: unknown) {
            console.error("Fetch Social Connect URL Error:", error);
            const errorMessage =
                "Error fetching social connect URL. Please try again or go to settings to connect your social accounts.";
            toast.error(<CustomToast message={errorMessage} type="error" />, {
                autoClose: 3000,
            });
        } finally {
            setConnectLoading(false);
        }
    };

    return (
        <>
            <div className="flex justify-center items-center space-x-4">
                <Button
                    onClick={() => {
                        // MB-1372: users who opted out of the guide go
                        // straight to the connection flow — no popup.
                        if (connectionGuidePref.isHidden()) {
                            proceedToConnection();
                        } else {
                            setShowWizard(true);
                        }
                    }}
                    disabled={connectLoading || disabled}
                    className="flex items-center justify-center"
                >
                    {connectLoading ? (
                        <>
                            <LoaderCircle className="animate-spin h-5 w-5 mr-2" />
                            Connecting...
                        </>
                    ) : (
                        text
                    )}
                </Button>
            </div>
            <SocialMediaHelpOverlay
                isOpen={showWizard}
                setIsOpen={setShowWizard}
                proceedToConnection={proceedToConnection}
            />
        </>
    );
};

export default ConnectSocialAccountsButton;
